import type React from "react";
import { Badge, Card } from "@pericles/framework-frontend";

import type {
  MeetingAudioBehaviorAnalysis,
  MeetingAudioSession,
} from "../types/meetings";

import { InlineAlert } from "./InlineAlert";
import { Button } from "./ui";

interface MeetingCoachPanelProps {
  analysis: MeetingAudioBehaviorAnalysis | null;
  audioSession: MeetingAudioSession | null;
  isRecording: boolean;
  recordingElapsedMs: number;
  templateKey?: string | null;
  error?: string | null;
  isLoading?: boolean;
  onRefresh?: () => void;
}

function formatDuration(valueMs: number | undefined): string {
  const safeMs = Math.max(0, Math.round(valueMs ?? 0));
  const totalSeconds = Math.floor(safeMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function scoreVariant(score: number): React.ComponentProps<typeof Badge>["variant"] {
  if (score >= 75) return "success";
  if (score >= 50) return "default";
  return "danger";
}

function signalToneToBadgeVariant(tone: "positive" | "neutral" | "warning"): React.ComponentProps<typeof Badge>["variant"] {
  if (tone === "positive") return "success";
  if (tone === "warning") return "danger";
  return "default";
}

function buildLiveTips(templateKey: string | null | undefined, elapsedMs: number): string[] {
  const minutes = Math.floor(elapsedMs / 60000);
  const phase = minutes < 5 ? "opening" : minutes < 25 ? "middle" : "closing";
  const template = String(templateKey ?? "").trim().toLowerCase();

  if (template === "retro") {
    if (phase === "opening") {
      return [
        "State the objective and the rules of the retrospective before diving into details.",
        "Ask for one concrete win and one concrete pain point early.",
      ];
    }
    if (phase === "middle") {
      return [
        "Keep the team on facts, not vague opinions.",
        "Convert complaints into causes, patterns, and possible fixes.",
      ];
    }
    return [
      "Leave with one owner, one action, and one follow-up date.",
      "Do not end on observations only. Force one commitment.",
    ];
  }

  if (template === "delivery") {
    if (phase === "opening") {
      return [
        "Clarify the target milestone, scope boundary, and who owns the decision.",
        "Surface blockers before discussing solutions.",
      ];
    }
    if (phase === "middle") {
      return [
        "Stress-test dependencies, risks, and sequencing.",
        "Push for dates, owners, and tradeoffs instead of general statements.",
      ];
    }
    return [
      "Close on the next checkpoint, the deliverable, and the accountable owner.",
      "Repeat the critical path out loud before ending the meeting.",
    ];
  }

  if (phase === "opening") {
    return [
      "Clarify the business objective, pain, and desired outcome within the first minutes.",
      "Ask open questions before pitching solutions.",
    ];
  }
  if (phase === "middle") {
    return [
      "Go deeper on pain, urgency, budget, and decision process.",
      "Let the client finish their thought before reframing.",
    ];
  }
  return [
    "Lock the next step with owner, deliverable, and date.",
    "Summarize the client's pain and confirm alignment before closing.",
  ];
}

function speakerLabel(role: string): string {
  if (role === "self") return "Me";
  if (role === "client") return "Client";
  if (role === "other") return "Other";
  return "Unknown";
}

export function MeetingCoachPanel({
  analysis,
  audioSession,
  isRecording,
  recordingElapsedMs,
  templateKey,
  error,
  isLoading = false,
  onRefresh,
}: MeetingCoachPanelProps): JSX.Element {
  const liveTips = buildLiveTips(templateKey, recordingElapsedMs);

  return (
    <Card title="Meeting coach">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge label={isRecording ? "Recording live" : analysis ? "Debrief ready" : "Waiting for transcript"} variant={isRecording ? "success" : "default"} />
            {audioSession ? <Badge label={`Session ${audioSession.status}`} variant="default" /> : null}
            <Badge label={`Elapsed ${formatDuration(recordingElapsedMs)}`} variant="default" />
            {analysis ? <Badge label={`Overall ${analysis.scorecard.overall}/100`} variant={scoreVariant(analysis.scorecard.overall)} /> : null}
          </div>
          {onRefresh ? (
            <Button type="button" variant="secondary" onClick={onRefresh} disabled={isLoading}>
              {isLoading ? "Refreshing..." : "Refresh coach"}
            </Button>
          ) : null}
        </div>

        {error ? <InlineAlert tone="danger" title={error} /> : null}

        <div className="rounded border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs font-semibold uppercase text-slate-500">Live guidance</div>
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            {liveTips.map((tip) => (
              <div key={tip} className="rounded border border-slate-200 bg-white p-2 text-sm text-slate-700">
                {tip}
              </div>
            ))}
          </div>
        </div>

        {!analysis ? (
          <div className="rounded border border-dashed border-slate-200 p-3 text-sm text-slate-600">
            Finish a diarized transcript to unlock behavior analysis, client signals, and coaching recommendations.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded border border-slate-200 bg-white p-3">
              <div className="text-xs font-semibold uppercase text-slate-500">Executive summary</div>
              <div className="mt-2 text-sm text-slate-800">{analysis.summary}</div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded border border-slate-200 bg-slate-50 p-2">
                  <div className="text-xs uppercase text-slate-500">Talk balance</div>
                  <div className="text-lg font-semibold text-slate-900">{analysis.scorecard.talkBalance}/100</div>
                </div>
                <div className="rounded border border-slate-200 bg-slate-50 p-2">
                  <div className="text-xs uppercase text-slate-500">Discovery</div>
                  <div className="text-lg font-semibold text-slate-900">{analysis.scorecard.discoveryQuality}/100</div>
                </div>
                <div className="rounded border border-slate-200 bg-slate-50 p-2">
                  <div className="text-xs uppercase text-slate-500">Client engagement</div>
                  <div className="text-lg font-semibold text-slate-900">{analysis.scorecard.clientEngagement}/100</div>
                </div>
                <div className="rounded border border-slate-200 bg-slate-50 p-2">
                  <div className="text-xs uppercase text-slate-500">Closing</div>
                  <div className="text-lg font-semibold text-slate-900">{analysis.scorecard.closingClarity}/100</div>
                </div>
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <div className="rounded border border-slate-200 bg-white p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-semibold uppercase text-slate-500">Client view</div>
                  <Badge label={`Engagement ${analysis.clientPerspective.engagementLevel}`} variant="default" />
                </div>
                <div className="mt-2 text-sm text-slate-800">{analysis.clientPerspective.summary}</div>
                <div className="mt-3 space-y-2 text-sm text-slate-700">
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Primary needs</div>
                    <div>{analysis.clientPerspective.primaryNeeds.join(", ") || "No strong needs detected yet."}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Buying signals</div>
                    <div>{analysis.clientPerspective.buyingSignals.join(", ") || "No major buying signal surfaced."}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Resistance</div>
                    <div>{analysis.clientPerspective.resistanceSignals.join(", ") || "No explicit resistance detected."}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Decision factors</div>
                    <div>{analysis.clientPerspective.decisionFactors.join(", ") || "Decision path still unclear."}</div>
                  </div>
                </div>
              </div>

              <div className="rounded border border-slate-200 bg-white p-3">
                <div className="text-xs font-semibold uppercase text-slate-500">Self review</div>
                <div className="mt-2 text-sm text-slate-800">{analysis.selfPerspective.summary}</div>
                <div className="mt-3 space-y-2 text-sm text-slate-700">
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Strengths</div>
                    <div>{analysis.selfPerspective.strengths.join(" ")}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Improvements</div>
                    <div>{analysis.selfPerspective.improvements.join(" ")}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Coach tips</div>
                    <div>{analysis.selfPerspective.coachTips.join(" ")}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <div className="rounded border border-slate-200 bg-white p-3">
                <div className="text-xs font-semibold uppercase text-slate-500">Recommended next questions</div>
                <div className="mt-3 space-y-3">
                  {analysis.recommendations.nextQuestions.length ? (
                    analysis.recommendations.nextQuestions.map((item) => (
                      <div key={item.id} className="rounded border border-slate-200 bg-slate-50 p-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-semibold text-slate-900">{item.question}</div>
                          <Badge label={item.priority} variant={item.priority === "high" ? "danger" : "default"} />
                        </div>
                        <div className="mt-1 text-sm text-slate-600">{item.why}</div>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-slate-600">No follow-up question generated yet.</div>
                  )}
                </div>
              </div>

              <div className="rounded border border-slate-200 bg-white p-3">
                <div className="text-xs font-semibold uppercase text-slate-500">Next actions</div>
                <div className="mt-3 space-y-2">
                  {analysis.recommendations.nextActions.length ? (
                    analysis.recommendations.nextActions.map((action) => (
                      <div key={action} className="rounded border border-slate-200 bg-slate-50 p-2 text-sm text-slate-700">
                        {action}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-slate-600">No next action generated yet.</div>
                  )}
                </div>
                <div className="mt-4 text-xs font-semibold uppercase text-slate-500">Live coach hints</div>
                <div className="mt-2 space-y-2">
                  {analysis.recommendations.liveCoachHints.length ? (
                    analysis.recommendations.liveCoachHints.map((hint) => (
                      <div key={hint} className="rounded border border-slate-200 bg-slate-50 p-2 text-sm text-slate-700">
                        {hint}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-slate-600">No live coach hint generated yet.</div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded border border-slate-200 bg-white p-3">
              <div className="text-xs font-semibold uppercase text-slate-500">Speakers</div>
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                {analysis.speakers.map((speaker) => (
                  <div key={speaker.speakerId} className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-slate-900">{speaker.label}</div>
                      <Badge label={`${speakerLabel(speaker.role)}${speaker.roleConfidence >= 0.55 ? " estimated" : ""}`} variant="default" />
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className="uppercase text-slate-500">Talk share</div>
                        <div className="text-sm font-semibold text-slate-900">{Math.round(speaker.talkShare * 100)}%</div>
                      </div>
                      <div>
                        <div className="uppercase text-slate-500">Turns</div>
                        <div className="text-sm font-semibold text-slate-900">{speaker.turnCount}</div>
                      </div>
                      <div>
                        <div className="uppercase text-slate-500">Questions</div>
                        <div className="text-sm font-semibold text-slate-900">{speaker.questionCount}</div>
                      </div>
                      <div>
                        <div className="uppercase text-slate-500">Open questions</div>
                        <div className="text-sm font-semibold text-slate-900">{speaker.openQuestionCount}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <div className="rounded border border-slate-200 bg-white p-3">
                <div className="text-xs font-semibold uppercase text-slate-500">Signals</div>
                <div className="mt-3 space-y-2">
                  {analysis.signals.length ? (
                    analysis.signals.map((signal) => (
                      <div key={signal.id} className="rounded border border-slate-200 bg-slate-50 p-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge label={signal.title} variant={signalToneToBadgeVariant(signal.tone)} />
                          {signal.role ? <Badge label={speakerLabel(signal.role)} variant="default" /> : null}
                        </div>
                        <div className="mt-2 text-sm text-slate-700">{signal.excerpt}</div>
                        <div className="mt-1 text-xs text-slate-500">{signal.rationale}</div>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-slate-600">No strong signal detected in the conversation.</div>
                  )}
                </div>
              </div>

              <div className="rounded border border-slate-200 bg-white p-3">
                <div className="text-xs font-semibold uppercase text-slate-500">Timeline</div>
                <div className="mt-3 space-y-2">
                  {analysis.timeline.length ? (
                    analysis.timeline.map((item) => (
                      <div key={item.phase} className="rounded border border-slate-200 bg-slate-50 p-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-semibold text-slate-900">{item.phase}</div>
                          <Badge label={item.themes.join(", ") || "general"} variant="default" />
                        </div>
                        <div className="mt-1 text-sm text-slate-700">{item.summary}</div>
                        <div className="mt-1 text-xs text-slate-500">{item.coachTip}</div>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-slate-600">No timeline summary available.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
